import { DOMAIN, DOMAIN_COUNTRY_MAP } from '@/src/constants/domain'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { VARIANT } from '@/src/utils/buildUtils'
import { getAppAgent } from '@/src/utils/deviceType'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'

/**
 * Domain code of the host, for `x-ct-domain-code` — Flyin hosts as `FSA`/`FEG`/`FAE`,
 * Cleartrip ones as a bare ISO-2 code, exactly as the map holds them.
 *
 * Left unset, the upstream infers it (`getDomainCode` in me-flyin
 * `flyin_modules/helperAdaptor/index.js`), which returns `FSA` for anything it cannot place
 * — so Egypt and UAE hosts are served Saudi inventory and pricing. It is what the upstream
 * keys its hotels API key off together with `x-ct-channel`, and also what it derives the
 * selling country from, by dropping the `F` — a country header of its own would be the same
 * fact spelled twice, free to disagree.
 */
const getUpstreamDomainCode = (): string => DOMAIN_COUNTRY_MAP[DOMAIN] ?? ''

/**
 * Sales channel, for `x-ct-channel`. Three values, matching the upstream's key table.
 * `getAppAgent` reports the two native agents separately, a distinction that table does not
 * draw, so they collapse to MOBILE here rather than at the far end.
 */
const getUpstreamChannel = (): string => {
    const appAgent = getAppAgent(getDeviceType())
    return appAgent === 'AndroidApp' || appAgent === 'iPhoneApp' ? 'MOBILE' : appAgent
}

/**
 * Host the request is being made from, for `x-ct-domain-name`. The upstream forwards it to
 * Cleartrip as `X-CT-DOMAIN`, which is otherwise a hardcoded 'flyin.com'.
 *
 * It cannot read the host itself: this app calls its own origin and Next rewrites to the
 * middleware, so the Host the middleware sees is its own service — behind the ingress an
 * internal load balancer name.
 *
 * Not `x-ct-domain`: that name is already in use on the itinerary endpoints, where it
 * carries the brand ('cleartrip' / 'flyin') rather than a host.
 */
const getUpstreamDomainName = (): string => (typeof window === 'undefined' ? '' : window.location.hostname)

// Mirrors the me-react-native hotels client contract (src/api/client.ts). The Flyin
// hotels upstream rejects traffic with `{status:'ERROR',code:'1600','ACCESS DENIED!'}`
// unless it is identified as native-app API traffic via `new-native-app`. `domain`,
// `x-ct-sourcetype` (brand) and `currency` drive domain-correct inventory and pricing
// (e.g. AED vs SAR), so they are sent on every hotels upstream request.
export const setHotelUpstreamHeaders = (headers: Headers, currency: string): Headers => {
    headers.set('Accept', 'application/json')
    headers.set('new-hotel-flow', 'true')
    headers.set('x-ct-sourcetype', VARIANT)
    const domainName = getUpstreamDomainName()
    if (domainName) headers.set('x-ct-domain-name', domainName)
    const domainCode = getUpstreamDomainCode()
    if (domainCode) headers.set('x-ct-domain-code', domainCode)
    // `device` below is a fixed 'a' and says nothing about the channel. No window
    // server-side, so no channel to state.
    if (typeof window !== 'undefined') headers.set('x-ct-channel', getUpstreamChannel())
    headers.set('ab-otp', 'b')
    headers.set('device', 'a')
    if (currency) headers.set('currency', currency)
    headers.set('Content-Language', getCurrentLocale())
    return headers
}
